import ColumnStore from '../data/ColumnStore.js';
import ChartColumn from './ChartColumn.js';

/**
 * @module Grid/column/SparklineColumn
 */

const
    allowedChartTypes = ['line', 'bar', 'pie'],
    lightGray = '#cccccc';

/**
 * Displays a sparkline (a small inline chart that gives an at-a-glance view of trends in a data series) in a grid cell.
 *
 * {@inlineexample Grid/column/SparklineColumn.js}
 *
 * The column's {@link #config-field} should be a property on your Grid data model containing an array of numbers that
 * make up the data series to display in the sparkline.
 *
 * SparklineColumn supports {@link #config-chartType}s of `line`, `bar`, and `pie`.
 *
 * ```javascript
 * new Grid({
 *     columns : [
 *         {
 *              type      : 'sparkline',
 *              chartType : 'line',
 *              field     : 'values' // field named here should contain
 *                                   // an array of numbers
 *         }
 *     ]
 * });
 * ```
 *
 * {@note}
 * Note that this column requires using thin bundles or thin npm packages in your application, and importing the `chart`
 * bundle / package. It does not work with normal packages, module or umd bundles.
 * {/@note}
 *
 * @extends Grid/column/ChartColumn
 * @classtype sparkline
 * @column
 */
export default class SparklineColumn extends ChartColumn {
    //region Config

    static $name = 'SparklineColumn';

    static type = 'sparkline';

    static fields = ['chart', 'chartType', 'chartCls', 'field', 'highlightMinMax', 'highlightColor'];

    static defaults = {
        /**
         * @hideconfigs widgets
         */

        /**
         * The type of chart.
         * @config {'line'|'bar'|'pie'}
         * @default
         */
        chartType : 'line',

        /**
         * Whether to highlight the minimum and maximum values in {@link #config-highlightColor}. Applicable for
         * {@link #config-chartType}s `line` and `bar`.
         * @config {Boolean}
         * @default
         */
        highlightMinMax : true,

        /**
         * The hex color to use to highlight the minimum and maximum values when {@link #config-highlightMinMax} is
         * `true`. Applicable for {@link #config-chartType}s `line` and `bar`.
         * @config {String}
         * @default
         */
        highlightColor : '#ff0000'
    };

    construct(...args) {
        super.construct(...args);

        const
            me         = this,
            modelClass = me.grid?.store.modelClass;

        me.widgets[0].cls = me.chartCls;

        if (!me.field) {
            console.warn(me.$$name + ' MUST be configured with a `field`.');
        }
        else if (!allowedChartTypes.includes(me.chartType)) {
            console.warn(me.$$name + ` chartType must be one of: ${allowedChartTypes.map(JSON.stringify).join(', ')}`);
        }
        else if (modelClass && !modelClass.fieldMap[me.field] && !me.constructor.suppressNoModelFieldWarning) {
            console.warn(me.$$name + ' is configured with a field, but this is not part of your Model `fields` collection.');
        }
    }

    get chartConfig() {
        return {
            ...super.chartConfig,
            chartType    : this.chartType,
            showPoints   : true,
            chartPadding : {
                top    : 4,
                left   : 0,
                right  : 0,
                bottom : 4
            },
            series : [{
                field            : 'v',
                label            : '',
                pointRadiusField : 'pointRadius',
                fillColorField   : 'fillColor',
                strokeColor      : this.chartType === 'line' ? lightGray : 'transparent'
            }],
            animate : true,
            labels  : [{
                field : 'v'
            }]
        };
    }

    getChartData(record) {
        const values = super.getChartData(record);
        let
            min             = +Infinity,
            max             = -Infinity,
            highlightMinMax = this.highlightMinMax && (this.chartType !== 'pie');

        if (highlightMinMax) {
            for (const v of values) {
                min = Math.min(min, v);
                max = Math.max(max, v);
            }
        }

        return values.map(v => {
            const highlight = highlightMinMax && (v === min || v === max);
            return {
                v,
                pointRadius : highlight ? 2 : 0,
                fillColor   : highlight ? this.highlightColor : undefined
            };
        });
    }
}

ColumnStore.registerColumnType(SparklineColumn, true);
